// Statistical helpers for the billing module.
//
// Coefficient of variation : ratio of standard deviation to mean, in %.
// Computed across the full 12-month series (including zero months) so the
// metric reflects concentration : a perfectly flat 12-month series → 0 %,
// a single-month spike → > 300 %, a 4-quarter pattern → ≈ 140 %.

const MONTHS = 12;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function coeffOfVariation(values) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  const nums = values.slice(0, MONTHS).map(num);
  // Pad to 12 if shorter
  while (nums.length < MONTHS) nums.push(0);
  const sum = nums.reduce((s, v) => s + v, 0);
  if (sum <= 0) return 0;
  const mean = sum / MONTHS;
  if (mean === 0) return 0;
  const variance = nums.reduce((s, v) => s + (v - mean) ** 2, 0) / MONTHS;
  const stdDev = Math.sqrt(variance);
  return (stdDev / mean) * 100;
}

// Rescale a 12-month series so its sum equals `newTotal`.
// - If the current series has any positive values, each value is multiplied
//   by newTotal/currentTotal — preserves the seasonal profile.
// - If the current total is zero (all months at 0), distribute uniformly
//   across all 12 months.
// Integer rounding residue is absorbed on the last non-zero month so the
// resulting sum equals newTotal exactly.
export function scaleToTotal(currentValues, newTotal) {
  const safe = (currentValues || []).slice(0, MONTHS).map(num);
  while (safe.length < MONTHS) safe.push(0);
  const target = Math.round(num(newTotal));
  const current = safe.reduce((s, v) => s + v, 0);

  if (target === 0) return Array(MONTHS).fill(0);

  let scaled;
  if (current > 0) {
    scaled = safe.map((v) => Math.round((v * target) / current));
  } else {
    const perMonth = Math.round(target / MONTHS);
    scaled = Array(MONTHS).fill(perMonth);
  }

  // Reconcile integer rounding so the sum is exactly target.
  const diff = target - scaled.reduce((s, v) => s + v, 0);
  if (diff !== 0) {
    // Apply diff on the last non-zero month, or the last month if all zero.
    let idx = MONTHS - 1;
    for (let i = MONTHS - 1; i >= 0; i -= 1) {
      if (scaled[i] !== 0) { idx = i; break; }
    }
    scaled[idx] += diff;
  }
  return scaled;
}
